import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { CustomerProfileInput, CustomerProfileResponse } from '@seaa/shared/contracts'
import { ApiError, api } from '../../lib/api'
import { authClient } from '../../lib/auth-client'

export const customerProfileKey = (userId: string | null) => ['customer-profile', userId] as const

export function isCustomerIdentityError(error: unknown) {
  return error instanceof ApiError && [
    'authentication_required', 'verified_phone_required', 'customer_profile_required',
    'customer_session_changed', 'booking_account_mismatch', 'customer_profile_changed',
  ].includes(error.code)
}

export function fetchCustomerProfile(signal?: AbortSignal) {
  return api<CustomerProfileResponse>('/api/account/profile', { cache: 'no-store', ...(signal ? { signal } : {}) })
}

export function saveCustomerProfile(userId: string, input: CustomerProfileInput) {
  return api<CustomerProfileResponse>('/api/account/profile', {
    method: 'PATCH', headers: { 'x-seaa-customer-id': userId }, body: JSON.stringify(input),
  })
}

export function useCustomerProfile() {
  const session = authClient.useSession()
  const queryClient = useQueryClient()
  const userId = !session.isPending && !session.error ? session.data?.user.id ?? null : null
  const query = useQuery({
    queryKey: customerProfileKey(userId),
    queryFn: ({ signal }) => fetchCustomerProfile(signal),
    enabled: Boolean(userId), retry: false, staleTime: 0, gcTime: 0,
    refetchOnMount: 'always', refetchOnWindowFocus: 'always',
  })
  // A failed session/profile refresh must never reveal the previous account's cache.
  const data = userId && !query.isError && query.data?.user.id === userId ? query.data : undefined
  return {
    data,
    userId,
    isLoading: session.isPending || (Boolean(userId) && query.isPending),
    isRefreshing: session.isRefetching || query.isFetching,
    error: session.error ?? query.error,
    needsVerification: !session.isPending && !session.error && (!userId || isCustomerIdentityError(query.error)),
    async refresh() {
      queryClient.removeQueries({ queryKey: ['customer-profile'] })
      queryClient.removeQueries({ queryKey: ['customer-account'] })
      const current = await fetchCustomerProfile()
      await session.refetch()
      queryClient.setQueryData(customerProfileKey(current.user.id), current)
      return current
    },
    async retry() {
      await session.refetch()
      await query.refetch()
    },
  }
}
